import React, { useCallback, useEffect, useState } from 'react';
import useAppLanguage from '@/hooks/useAppLanguage';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import debounce from 'lodash.debounce';
import ScrolltoElement from '@/components/ScrollToElement/ScrollToElement';
import { CHINA_COMBO_ORDER, DESCRIPTION, errorTypes, EXTERNAL, formCRTypes, formYESNO, INTERNAL, TAB_FORMS_OPTIONS } from '@/helpers/constants';
import { lateralMenu } from '@/helpers/opensearch-utils';
import useAuth from '@/hooks/useAuth';
import useError from '@/hooks/useError';
import useToastContext from '@/hooks/useToastContext';
import { setClassificationForms, setAffectedVehiclesForms, setDescriptionForms } from '@/redux/Forms/forms.action';
import { getClassificationForms, getAffectedVehiclesForms, getDescriptionForms } from '@/redux/Forms/forms.reducer';
import { setOverviewFilters, setOverviewFiltersFocus } from '@/redux/OverviewFilters/overviewFilters.action';
import { getFilterOverview, getFilterSubCROverview, getFilterToFocus, getPillOverview, getPillSubCROverview } from '@/redux/OverviewFilters/overviewFilters.reducer';
import administrationServices from '@/services/administrationServices';
import affectedVehicleServices from '@/services/affectedVehicleService';
import campaignServices from '@/services/campaignServices';
import Dropdown from '../Dropdown/Dropdown';
import InputText from '../InputText/InputText';
import MultiSelection from '../MultiSelection/MultiSelection';
import SimpleButton from '../SimpleButton/SimpleButton';
import Spinner from '../Spinner/Spinner';
import TooltipComponent from '../Tooltip/TooltipComponent';
import TabLabelIcon from '../../TabLabel/TabLabelIcon';

const type = { MULTISELECT: 'Multiselect', DROPDOWN: 'Dropdown', TEXT: 'Text' };

const OPTIONS = {
  AFFECTED_MODEL_SERIES: { label: 'affectedVehicles.affectedModelSeries', type: type.MULTISELECT },
  AFFECTED_PLANTS: { label: 'hardware.affectedPlants', type: type.MULTISELECT },
  CLASSIFICATION: { label: 'classification.classification', type: type.MULTISELECT },
  TRIGGER_SOURCE: { label: 'summary.triggerSource', type: type.DROPDOWN },
  GB18285_RELEVANCE: { label: 'classification.GB18285_Relevance', type: type.DROPDOWN },
  BLOCKING_RECOMMENDATION: { label: 'classification.blockingRecommendation', type: type.DROPDOWN },
  CAUSER: { label: 'involvedParties.causerInternalExternal', type: type.DROPDOWN },
  CUSTOMER_RELEVANCY: { label: 'classification.customerRelevancy', type: type.DROPDOWN },
  POSSIBLE_CAUSER: { label: 'involvedParties.possibleCauser', type: type.TEXT },
  SUPPLIER_NO: { label: 'dialog.supplierNumber', type: type.TEXT },
  SUPPLIER_NAME: { label: 'dialog.supplierName', type: type.TEXT },
  TIER1_SUPPLIER_NAME: { label: 'dialog.tier1SupplierName', type: type.TEXT },
  TIER1_SUPPLIER_NO: { label: 'dialog.tier1SupplierNumber', type: type.TEXT },
  CONTACT_PERSON_MP: { label: 'dialog.contactPersonMP', type: type.TEXT },
  CAMPAIGN_NUMBER: { label: 'feedback.campaignNumber', type: type.TEXT },
  IRA_NO: { label: 'involvedParties.iraNumber', type: type.TEXT },
  PART_NO: { label: 'hardware.numberNew', type: type.TEXT },
  FREE_TEXT: { label: 'search', type: type.TEXT },
  MB_OS_RELEVANCY: { label: 'software.MBOSOtaRevelancy', type: type.DROPDOWN },
  NONE: { label: 'menu.selectFilters' },
  TYPE: { label: 'campaignRequests.type', type: type.DROPDOWN },
};

export const INTERNAL_EXTERNAL = {
  [INTERNAL]: { EN: 'Internal', DE: 'Intern' },
  [EXTERNAL]: { EN: 'External', DE: 'Extern' },
};

const useStyles = makeStyles(({ cartTheme }) => ({
  form: {
    width: '100%',
  },
  select: {
    height: '100%',
  },
  flex: {
    height: 'calc(100% - 70px)',
    display: 'flex',
    flexDirection: 'column',
    width: '300px',
  },
  Separator: {
    borderBottom: '#ababab',
    borderBottomStyle: 'solid',
    borderBottomWidth: '0.1vh',
    paddingTop: '12px',
    marginLeft: '-20px',
  },
  FiltersField: {
    paddingLeft: '20px',
    paddingBottom: '20px',
  },
  MidleSectionFilters: {
    marginBottom: '3px',
  },
  bigRow: (props) => ({
    maxHeight: props.isMultiRoleUser ? 'calc(100% - 115px)' : 'calc(100% - 70px)',
    overflow: 'auto',
    borderBottom: '#ababab',
    borderBottomStyle: 'solid',
    borderBottomWidth: '0.1vh',
  }),
  smallRow: {
    height: '70px',
    marginTop: '2vh',
  },
  buttonReset: {
    bottom: '10px',
    alignSelf: 'center',
    color: cartTheme.primaryColor,
  },
  chip: {
    margin: 8,
    height: '34px',
    borderRadius: '4px',
  },
  SpinnerHeader: {
    alignItems: 'center',
  },
  placeHolder: {
    fontFamily: 'DaimlerCS-Regular, DaimlerCS-Demi',
    paddingLeft: '10px',
    fontSize: '18px',
    fontStretch: '100%',
    fontStyle: 'normal',
    color: '#00000061',
  },
  searchFilter: {
    width: '90%',
    marginTop: '14px',
  },
  filters: {
    width: '100%',
    '& .MuiSelect-selectMenu': {
      paddingBottom: '14px',
      paddingTop: '14px',
    },
  },
  spinner: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
  inputLabelFreeText: {
    pointerEvents: 'auto',
  },
  tooltipFreeText: {
    maxWidth: 'unset',
  },
  infoIconContainer: {
    marginLeft: '10px',
  },
  activeFilter: {
    '& .MuiInputBase-root': {
      backgroundColor: cartTheme.highlightedFilter,
    },
  },
  resetFilterIcon: {
    color: cartTheme.primaryColor,
    zoom: 0.75,
  },
}));

const FilterSelection = (props) => {
  const { classificationForms, setClassificationForms, setAffectedForms, descriptionforms, setFormsDescription,
    filterOverview, setGlobalFilters, pillFilterOverview, isSubCROverview, filterSubCROverview, pillSubCRFilterState,
    badgeCount, filterToFocus, setOverviewFiltersFocus } = props;
  const [filter, pillFilter] = isSubCROverview
    ? [filterSubCROverview, pillSubCRFilterState]
    : [filterOverview, pillFilterOverview];
  const [filterName, pillFilterName] = isSubCROverview
    ? ['filterSubCampaignOverview', 'pillFilterSubCampaignOverview']
    : ['filterOverview', 'pillFilterOverview'];
  const { t } = useTranslation();
  const lang = useAppLanguage();
  const [selectionValues, setSelectionValues] = useState({});
  const [modelSeriesOptions, setModelSeriesOptions] = useState([]);
  const [plantsOptions, setPlantsOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClassificationDisabled, setClassificationDisabled] = useState(false);
  const [isCustomerDisabled, setIsCustomerDisabled] = useState(false);
  const [filterOpen, setFilterOpen] = useState(null);
  const { handleCriticalError } = useError();
  const { addToast } = useToastContext();
  const { checkMultiRoleUser: isMultiRoleUser } = useAuth();
  const classes = useStyles({ isMultiRoleUser });
  const arrangedChinaOptions = CHINA_COMBO_ORDER.reduce((prev, newKey) => (
    { ...prev, [newKey]: classificationForms.affectsChina?.options[newKey] }
  ), {});

  const debouncedSetConfig = useCallback(
    debounce((value) => setGlobalFilters(value), 1000), [],
  );

  const handleChangeValues = (valueSelected, label) => {
    let newValue = valueSelected;
    const isDropdownFilter = lateralMenu.filter((item) => item.isDropdownFilter)?.[label];

    if (label !== 'FREE_TEXT' && OPTIONS[label]?.type === 'Text' && isDropdownFilter) {
      if (valueSelected && valueSelected.match(/^[a-zA-Z\d\-_.,\s]+$/) === null) {
        newValue = selectionValues[label];
      }
    }

    if (label === 'FREE_TEXT') {
      const SPECIAL_CHAR_REGEX = new RegExp(/[\^"§$%&/()={}\\`~',;.:_@+<>|·´¨çÇ`^¡¿~ñºª€Ñ¬]/g);

      if (newValue.match(SPECIAL_CHAR_REGEX)) {
        addToast(t('alert.charNotAllowedFreeText'), 'Warning', errorTypes.CHAR_NOT_ALLOWED_FREETEXT);

        return;
      }
    }

    if (label === 'CLASSIFICATION') {
      setIsCustomerDisabled(newValue.length > 0);
    }

    if (label === 'CUSTOMER_RELEVANCY') {
      setClassificationDisabled(newValue.length > 0);
    }

    let newValueSelected = null;

    if (label === 'AFFECTED_PLANTS') {
      const founded = newValue.filter((item) => newValue.filter((val) => val.wkzNumber === item.wkzNumber).length > 1);
      newValueSelected = newValue.filter((val) => !founded.includes(val));
    }

    const newState = {
      ...selectionValues,
      [label]: newValueSelected || newValue,
    };

    setSelectionValues(newState);

    const newFilter = filter?.filter((filt) => !Object.keys(newState).includes(filt.columnName)) || [];

    const configFilter = Object.entries(newState)
      .filter((val) => val[1])
      .map(([columnName, value]) => ({ columnName, value }))
      .filter(({ value }) => value.length > 0);

    debouncedSetConfig({ [filterName]: JSON.stringify([...newFilter, ...configFilter]) });
  };

  const orderOptions = (options) => {
    const keys = Object.keys(options || {}).reverse();

    return keys?.reduce((acc, label) => ({
      ...acc,
      [label]: options?.[label],
    }), {});
  };

  const handleDeleteChip = (module, chipToDelete) => {
    const newSelections = selectionValues[module]
      .filter((model) => (module === 'AFFECTED_PLANTS' ? model.plantName !== chipToDelete : model !== chipToDelete));
    handleChangeValues(newSelections, module);
  };

  const resetFilter = () => {
    if (filter || pillFilter) {
      setSelectionValues({});
      setClassificationDisabled(false);
      setIsCustomerDisabled(false);
      setGlobalFilters({ [filterName]: JSON.stringify(filter?.map((f) => ({ columnName: f.columnName, value: '' })) || []), [pillFilterName]: null });
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([campaignServices.getTabForms(TAB_FORMS_OPTIONS.CAMP_CLASSIFICATION),
      affectedVehicleServices.getModelSeries(false),
      affectedVehicleServices.getOnlyPlantsOptions(false),
      campaignServices.getTabForms(TAB_FORMS_OPTIONS.CAMP_AFF_VEHICLES),
      administrationServices.getLastForms('CAMPAIGN_DESCRIPTION'),
    ]).then(([gbForms, modelSeries, plants, affectedVehForms, descriptionForms]) => {
      setClassificationForms(gbForms.form);
      setModelSeriesOptions(modelSeries);
      setPlantsOptions(plants);
      setAffectedForms(affectedVehForms.form);
      setFormsDescription(descriptionForms.form);
    }).catch(handleCriticalError)
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    const filterr = filter?.map((f) => ({ columnName: f.columnName, value: f.value })) || [];
    const valueFilter = filterr.filter((element) => element?.columnName?.match(/^[A-Z]/));

    let newFilters = {};
    valueFilter.forEach((filt) => {
      newFilters = ({ ...newFilters, [filt.columnName]: filt.value });
    });
    setSelectionValues(newFilters);
  }, [filter, pillFilter]);

  const filtersLength = Object.values(selectionValues).reduce((acc, value) => acc + value.length, 0);

  const isActive = (value) => {
    if (value) {
      return Object.keys(value).length > 0;
    }

    return false;
  };

  const getFieldClassName = (value, fieldClass = 'filters') => `${classes[fieldClass]} ${isActive(value) ? classes.activeFilter : ''}`;

  const MULTISELECT_FILTERS = [{
    id: 'affectedPlants',
    options: plantsOptions?.plants,
    dataPath: 'AFFECTED_PLANTS',
    label: 'hardware.affectedPlants',
    extraProps: { isPlants: true },
  }, {
    id: 'classification',
    options: classificationForms?.classification?.options,
    dataPath: 'CLASSIFICATION',
    label: 'classification.classification',
    extraProps: { disabled: isClassificationDisabled },
  }, {
    id: 'affectedModelSeries',
    options: modelSeriesOptions,
    dataPath: 'AFFECTED_MODEL_SERIES',
    label: 'affectedVehicles.affectedModelSeries',
    extraProps: { isModelSeries: true },
  }];

  const DROPDOWN_FILTERS = [{
    id: 'triggerSource',
    options: descriptionforms?.source?.options,
    dataPath: 'TRIGGER_SOURCE',
  }, {
    id: 'affectsChina',
    options: arrangedChinaOptions,
    dataPath: 'GB18285_RELEVANCE',
  }, {
    id: 'blockingRecommendation',
    options: orderOptions(classificationForms?.blockingRecommendation?.options || {}),
    dataPath: 'BLOCKING_RECOMMENDATION',
  }, {
    id: 'customerRelevance',
    options: classificationForms?.customerRelevancy?.options,
    dataPath: 'CUSTOMER_RELEVANCY',
    disabled: isCustomerDisabled,
  }, {
    id: 'locationInternal',
    options: INTERNAL_EXTERNAL,
    dataPath: 'CAUSER',
  }, {
    id: 'mbosRelevance',
    options: formYESNO,
    dataPath: 'MB_OS_RELEVANCY',
  }, {
    id: 'type',
    options: formCRTypes,
    dataPath: 'TYPE',
  },
  ];

  const INPUT_TEXT_FILTERS = [{
    id: DESCRIPTION.POSSIBLE_CAUSER,
    dataPath: 'POSSIBLE_CAUSER',
  }, {
    id: 'supplierNumber',
    dataPath: 'SUPPLIER_NO',
  }, {
    id: 'supplierName',
    dataPath: 'SUPPLIER_NAME',
  }, {
    id: DESCRIPTION.TIER1_SUPPLIER.NAME,
    dataPath: 'TIER1_SUPPLIER_NAME',
  }, {
    id: DESCRIPTION.TIER1_SUPPLIER.NUMBER,
    dataPath: 'TIER1_SUPPLIER_NO',
  },
  {
    id: DESCRIPTION.CONTACT_PERSON_MP,
    dataPath: 'CONTACT_PERSON_MP',
  }, {
    id: 'campaign',
    dataPath: 'CAMPAIGN_NUMBER',
  }, {
    id: DESCRIPTION.IRA_NUMBER,
    dataPath: 'IRA_NO',
  }, {
    id: 'partNumbers',
    dataPath: 'PART_NO',
  }];

  return (
    <div className={classes.flex}>
      <div className={classes.bigRow}>
        <div className={classes.FiltersField}>
          {isLoading && <div className={classes.spinner}><Spinner small isVisible /></div>}
          {!isLoading && (
            <>
              <InputText
                id="input-free-text"
                label={(
                  <>
                    {t(OPTIONS.FREE_TEXT.label)}
                    <TooltipComponent
                      classes={{ tooltip: classes.tooltipFreeText }}
                      arrow
                      title={(
                        <div><Typography variant="body2" component="span">{t('campaignRequests.infoFreeText')}</Typography></div>
                      )}
                    >
                      <span className={classes.infoIconContainer}><TabLabelIcon /></span>
                    </TooltipComponent>
                  </>
                )}
                handleChange={(ev) => handleChangeValues(ev.target.value, 'FREE_TEXT')}
                value={selectionValues?.FREE_TEXT || ''}
                type="search"
                icoSearch
                maxLength={256}
                className={getFieldClassName(selectionValues?.FREE_TEXT, 'searchFilter')}
                formControlClass={classes.searchFilter}
                inputLabelClasses={{ root: classes.inputLabelFreeText }}
              />
              <div className={classes.Separator} />
              {MULTISELECT_FILTERS.map(({ id, options, dataPath, label, extraProps }) => (
                <ScrolltoElement key={id} shouldFocus={filterToFocus === dataPath} resetState={setOverviewFiltersFocus}>
                  <MultiSelection
                    key={`${id}-${filtersLength}`} // Force render when filters change
                    open={filterOpen === id} // Keep it open
                    handleDeleteItem={(event) => handleDeleteChip(dataPath, event)}
                    id={`multiselect-filter-${id}`}
                    label={t(label)}
                    options={options || []}
                    handleChange={(event) => handleChangeValues(event.target.value, dataPath)}
                    value={selectionValues?.[dataPath] || []}
                    {...extraProps}
                    language={lang}
                    formControlClass={getFieldClassName(selectionValues?.[dataPath])}
                    onOpen={() => setFilterOpen(id)}
                    onClose={() => setFilterOpen(null)}
                  />
                </ScrolltoElement>
              ))}
              <div className={classes.MidleSectionFilters}>
                {DROPDOWN_FILTERS.map(({ id, options, dataPath, disabled }) => (
                  <ScrolltoElement key={id} shouldFocus={filterToFocus === dataPath} resetState={setOverviewFiltersFocus}>
                    <Dropdown
                      key={`${id}-${filtersLength}`} // Force render when filters change
                      id={id}
                      label={t(OPTIONS[dataPath].label)}
                      options={options ? { '': { [lang]: t('campaignRequests.all') }, ...options } : {}}
                      value={selectionValues?.[dataPath] || ''}
                      handleChange={(event) => handleChangeValues(event.target.value, dataPath)}
                      formControlClass={getFieldClassName(selectionValues?.[dataPath])}
                      disabled={disabled}
                    />
                  </ScrolltoElement>
                ))}
              </div>
              {INPUT_TEXT_FILTERS.map(({ id, dataPath }) => (
                <ScrolltoElement key={id} shouldFocus={filterToFocus === dataPath} resetState={setOverviewFiltersFocus}>
                  <InputText
                    key={id}
                    id={id}
                    label={t(OPTIONS[dataPath].label)}
                    handleChange={(ev) => handleChangeValues(ev.target.value, dataPath)}
                    value={selectionValues?.[dataPath] || ''}
                    type="search"
                    maxLength={256}
                    className={getFieldClassName(selectionValues?.[dataPath])}
                    formControlClass={classes.filters}
                  />
                </ScrolltoElement>
              ))}
            </>
          )}
        </div>
      </div>
      <div className={`${classes.buttonReset} ${classes.smallRow}`}>
        <SimpleButton
          id="btn-reset-filter"
          iconComponent={<i className={`di icon-filter-zuruecksetzen ${classes.resetFilterIcon}`} />}
          color="primary"
          textStyle={classes.resetFilterText}
          buttonText={t('campaignRequests.resetFilter')}
          handleClick={resetFilter}
          disabled={badgeCount === 0}
        />
      </div>
    </div>

  );
};

const mapStateToProps = (state) => ({
  classificationForms: getClassificationForms(state),
  affectedForms: getAffectedVehiclesForms(state),
  descriptionforms: getDescriptionForms(state),
  filterOverview: getFilterOverview(state),
  pillFilterOverview: getPillOverview(state),
  filterSubCROverview: getFilterSubCROverview(state),
  pillSubCRFilterState: getPillSubCROverview(state),
  filterToFocus: getFilterToFocus(state),
});

const actionCreators = {
  setClassificationForms: setClassificationForms,
  setAffectedForms: setAffectedVehiclesForms,
  setFormsDescription: setDescriptionForms,
  setGlobalFilters: setOverviewFilters,
  setOverviewFiltersFocus: setOverviewFiltersFocus,
};

export default connect(mapStateToProps, actionCreators)(FilterSelection);
